import { createAsyncThunk } from '@reduxjs/toolkit';
import GetJitFiltersService from 'modules/inbound/programCoverage/application/GetPartCoverageFiltersService';
import {
  NameValues,
  FiltersItem
} from 'modules/inbound/shared/domain/NameValues';
import { getSelectedSubheaderFilters } from '.';
import GetDateFormatedService from 'modules/inbound/shared/domain/GetDateFormatedService';
import { IDateRange } from 'modules/inbound/endOfProductionDate/domain/IEndOfProductionDateRepository';

const getPartCoverageFilters = createAsyncThunk(
  'partCoverageFiltersProgramCoverage/getPartCoverageFilters',
  async (_, { getState }) => {
    const appState = getState();
    const {
      selectedFamily,
      selectedLines,
      selectedProvidersTier1Ids,
      selectedLogisticOperatorsIds,
      selectedModels,
      selectedInbounders,
      selectedFlowType,
      selectedStatus,
      selectedPRs,
      selectedBoardKey,
      selectedCoverageStoppedAt,
      selectedQuantityStock
    } = getSelectedSubheaderFilters(appState);

    const fromDate = selectedCoverageStoppedAt.value.fromDate
      ? GetDateFormatedService.formatTimeToFullCustomDate(
          selectedCoverageStoppedAt.value.fromDate
        )
      : '';
    const toDate = selectedCoverageStoppedAt.value.toDate
      ? GetDateFormatedService.formatTimeToFullCustomDate(
          selectedCoverageStoppedAt.value.toDate
        )
      : '';
    const formatedCoverageStoppedAt: IDateRange = { fromDate, toDate };
    const queryFilters: FiltersItem[] = [
      {
        name: NameValues.FAMILY,
        values: selectedFamily
      },
      {
        name: NameValues.LINES,
        values: selectedLines
      },
      {
        name: NameValues.PROVIDER_TIER_1_ID,
        values: selectedProvidersTier1Ids.map(({ key, text }) => ({
          key: key.split('-')[0].trim(),
          text: text
        }))
      },
      {
        name: NameValues.LOGISTIC_OPERATOR_ID,
        values: selectedLogisticOperatorsIds
      },
      {
        name: NameValues.MODELS,
        values: selectedModels
      },
      {
        name: NameValues.INBOUNDER,
        values: selectedInbounders
      },
      {
        name: NameValues.PRS,
        values: selectedPRs
      },
      {
        name: NameValues.COVERAGE_STOPPED_AT,
        values: [
          formatedCoverageStoppedAt.fromDate,
          formatedCoverageStoppedAt.toDate
        ]
      },
      {
        name: NameValues.QUANTITYSTOCK,
        values: [
          selectedQuantityStock.value.Min,
          selectedQuantityStock.value.Max
        ]
      }
    ];
    !!selectedFlowType.key &&
      queryFilters.push({
        name: NameValues.FLOW_TYPE,
        values: [selectedFlowType]
      });

    !!selectedStatus.key &&
      queryFilters.push({
        name: NameValues.SEQUENCERCOVERAGESTATUS,
        values: [selectedStatus]
      });

    !!selectedBoardKey.key &&
      queryFilters.push({
        name: NameValues.IS_ON_LOCKS,
        values: [selectedBoardKey]
      });

    let querySearch: string[] = [];

    queryFilters.forEach((filter) => {
      const values = filter.values.map((item) => item?.key ?? item);
      const hasValues = filter.values.length > 0;

      if (!hasValues) return;

      const isLikeFilter =
        filter.name === NameValues.MODELS || filter.name === NameValues.PRS;
      const isInFilter = filter.name === NameValues.LINES;
      const isDateRangeFilter =
        filter.name === NameValues.COVERAGE_STOPPED_AT &&
        !!filter.values[0] &&
        !!filter.values[1];
      const isQuantityStockFilter =
        filter.name === NameValues.QUANTITYSTOCK &&
        !!filter.values[0] &&
        !!filter.values[1];
      if (isDateRangeFilter) {
        const fromDate = filter.values[0];
        const toDate = filter.values[1];
        querySearch.push(
          `coverage_stopped_at:>=:${fromDate}||coverage_stopped_at:<=:${toDate}`
        );
        return;
      }

      if (isQuantityStockFilter) {
        const min = filter.values[0];
        const max = filter.values[1];
        querySearch.push(`stock_quantity:>=:${min}||stock_quantity:<=:${max}`);
        return;
      }

      if (isLikeFilter) {
        querySearch.push(`${filter.name}:LK:(${values.join(';')})`);
        return;
      }

      if (
        (values.length > 1 || isInFilter) &&
        !!filter.values[0] &&
        !!filter.values[1]
      ) {
        querySearch.push(
          `${filter.name}:IN:(${values.map((v) => `"${v}"`).join(';')})`
        );
        return;
      }

      if (values.length === 1) {
        querySearch.push(`${filter.name}:=:"${values[0]}"`);
      }
    });

    const query = querySearch.filter((item) => item !== '').join('||');
    const queryParams = {
      search: query && `{${query}}`
    };
    const response = await GetJitFiltersService.execute({ queryParams });
    return response;
  }
);

const getPRFilter = createAsyncThunk(
  'PartCoverageProgramCoverage/getPartCoverageFilters/PRFilter',
  async (query: string) => {
    const response = await GetJitFiltersService.getPRFilter(query);
    return response;
  }
);

export { getPartCoverageFilters, getPRFilter };
